import React, { useEffect } from 'react';

const VisitorPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Visitor Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Visitor Pattern - define operations on elements without changing their classes. Learn with document processing and tax calculation examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Visitor Pattern - define operations on elements without changing their classes. Learn with document processing and tax calculation examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'visitor pattern, design patterns, behavioral patterns, double dispatch, operations on objects, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'visitor pattern, design patterns, behavioral patterns, double dispatch, operations on objects, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Visitor Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Define operations without changing element classes
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Visitor pattern allows you to define new operations without changing the classes 
            of the elements on which they operate. It separates algorithms from the objects on 
            which they operate by using double dispatch to determine the correct method to call.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You have a hierarchy of classes and need to perform various operations on them. Adding 
            new operations would require modifying all classes. The Visitor pattern allows you to 
            add new operations without changing existing classes.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Document Processing System</h2>
          <div style={styles.codeBlock}>
{`// Visitor interface
interface DocumentVisitor {
    void visit(TextElement element);
    void visit(ImageElement element);
    void visit(TableElement element);
    void visit(HeaderElement element);
}

// Element interface
interface DocumentElement {
    void accept(DocumentVisitor visitor);
}

// Concrete elements
class TextElement implements DocumentElement {
    private String content;
    private String fontFamily;
    private int fontSize;
    private boolean isBold;
    
    public TextElement(String content, String fontFamily, int fontSize, boolean isBold) {
        this.content = content;
        this.fontFamily = fontFamily;
        this.fontSize = fontSize;
        this.isBold = isBold;
    }
    
    @Override
    public void accept(DocumentVisitor visitor) {
        visitor.visit(this);
    }
    
    // Getters
    public String getContent() { return content; }
    public String getFontFamily() { return fontFamily; }
    public int getFontSize() { return fontSize; }
    public boolean isBold() { return isBold; }
    public int getWordCount() { return content.split("\\\\s+").length; }
}

class ImageElement implements DocumentElement {
    private String src;
    private int width;
    private int height;
    private String altText;
    private long fileSize; // in bytes
    
    public ImageElement(String src, int width, int height, String altText, long fileSize) {
        this.src = src;
        this.width = width;
        this.height = height;
        this.altText = altText;
        this.fileSize = fileSize;
    }
    
    @Override
    public void accept(DocumentVisitor visitor) {
        visitor.visit(this);
    }
    
    // Getters
    public String getSrc() { return src; }
    public int getWidth() { return width; }
    public int getHeight() { return height; }
    public String getAltText() { return altText; }
    public long getFileSize() { return fileSize; }
}

class TableElement implements DocumentElement {
    private int rows;
    private int columns;
    private String[][] data;
    private boolean hasHeader;
    
    public TableElement(int rows, int columns, boolean hasHeader) {
        this.rows = rows;
        this.columns = columns;
        this.hasHeader = hasHeader;
        this.data = new String[rows][columns];
    }
    
    public void setCellData(int row, int col, String value) {
        if (row < rows && col < columns) {
            data[row][col] = value;
        }
    }
    
    @Override
    public void accept(DocumentVisitor visitor) {
        visitor.visit(this);
    }
    
    // Getters
    public int getRows() { return rows; }
    public int getColumns() { return columns; }
    public String[][] getData() { return data; }
    public boolean hasHeader() { return hasHeader; }
    public int getCellCount() { return rows * columns; }
}

class HeaderElement implements DocumentElement {
    private String text;
    private int level; // 1-6 for H1-H6
    
    public HeaderElement(String text, int level) {
        this.text = text;
        this.level = Math.max(1, Math.min(6, level)); // Ensure level is 1-6
    }
    
    @Override
    public void accept(DocumentVisitor visitor) {
        visitor.visit(this);
    }
    
    // Getters
    public String getText() { return text; }
    public int getLevel() { return level; }
}

// Concrete visitors
class HTMLExportVisitor implements DocumentVisitor {
    private StringBuilder html = new StringBuilder();
    
    @Override
    public void visit(TextElement element) {
        html.append("<p style=\\"font-family: ").append(element.getFontFamily())
            .append("; font-size: ").append(element.getFontSize()).append("px");
        
        if (element.isBold()) {
            html.append("; font-weight: bold");
        }
        
        html.append("\\">")
            .append(element.getContent())
            .append("</p>\\n");
    }
    
    @Override
    public void visit(ImageElement element) {
        html.append("<img src=\\"").append(element.getSrc())
            .append("\\" width=\\"").append(element.getWidth())
            .append("\\" height=\\"").append(element.getHeight())
            .append("\\" alt=\\"").append(element.getAltText())
            .append("\\" />\\n");
    }
    
    @Override
    public void visit(TableElement element) {
        html.append("<table border=\\"1\\">\\n");
        String[][] data = element.getData();
        
        for (int i = 0; i < element.getRows(); i++) {
            html.append("  <tr>");
            for (int j = 0; j < element.getColumns(); j++) {
                String tag = (i == 0 && element.hasHeader()) ? "th" : "td";
                html.append("<").append(tag).append(">")
                    .append(data[i][j] != null ? data[i][j] : "")
                    .append("</").append(tag).append(">");
            }
            html.append("</tr>\\n");
        }
        html.append("</table>\\n");
    }
    
    @Override
    public void visit(HeaderElement element) {
        String tag = "h" + element.getLevel();
        html.append("<").append(tag).append(">")
            .append(element.getText())
            .append("</").append(tag).append(">\\n");
    }
    
    public String getHTML() {
        return html.toString();
    }
}

class WordCountVisitor implements DocumentVisitor {
    private int totalWords = 0;
    private int textElements = 0;
    private int headerElements = 0;
    
    @Override
    public void visit(TextElement element) {
        totalWords += element.getWordCount();
        textElements++;
    }
    
    @Override
    public void visit(ImageElement element) {
        // Images don't contribute to word count
        // But we could count alt text if needed
        if (element.getAltText() != null && !element.getAltText().isEmpty()) {
            totalWords += element.getAltText().split("\\\\s+").length;
        }
    }
    
    @Override
    public void visit(TableElement element) {
        // Count words in table cells
        String[][] data = element.getData();
        for (int i = 0; i < element.getRows(); i++) {
            for (int j = 0; j < element.getColumns(); j++) {
                if (data[i][j] != null && !data[i][j].isEmpty()) {
                    totalWords += data[i][j].split("\\\\s+").length;
                }
            }
        }
    }
    
    @Override
    public void visit(HeaderElement element) {
        totalWords += element.getText().split("\\\\s+").length;
        headerElements++;
    }
    
    public void printReport() {
        System.out.println("=== Word Count Report ===");
        System.out.println("Total words: " + totalWords);
        System.out.println("Text elements: " + textElements);
        System.out.println("Header elements: " + headerElements);
    }
}

class FileSizeCalculatorVisitor implements DocumentVisitor {
    private long totalSize = 0; // in bytes
    
    @Override
    public void visit(TextElement element) {
        // Estimate text size (UTF-8 encoding)
        totalSize += element.getContent().getBytes().length;
    }
    
    @Override
    public void visit(ImageElement element) {
        totalSize += element.getFileSize();
    }
    
    @Override
    public void visit(TableElement element) {
        // Estimate table data size
        String[][] data = element.getData();
        for (int i = 0; i < element.getRows(); i++) {
            for (int j = 0; j < element.getColumns(); j++) {
                if (data[i][j] != null) {
                    totalSize += data[i][j].getBytes().length;
                }
            }
        }
    }
    
    @Override
    public void visit(HeaderElement element) {
        totalSize += element.getText().getBytes().length;
    }
    
    public void printReport() {
        System.out.println("=== File Size Report ===");
        System.out.println("Total estimated size: " + totalSize + " bytes");
        System.out.println("Size in KB: " + String.format("%.2f", totalSize / 1024.0));
        System.out.println("Size in MB: " + String.format("%.2f", totalSize / (1024.0 * 1024.0)));
    }
}

// Document class to hold elements
class Document {
    private List<DocumentElement> elements = new ArrayList<>();
    
    public void addElement(DocumentElement element) {
        elements.add(element);
    }
    
    public void processWithVisitor(DocumentVisitor visitor) {
        for (DocumentElement element : elements) {
            element.accept(visitor);
        }
    }
}

// Usage
public class VisitorDemo {
    public static void main(String[] args) {
        // Create document
        Document document = new Document();
        
        // Add elements
        document.addElement(new HeaderElement("Introduction to Design Patterns", 1));
        document.addElement(new TextElement(
            "Design patterns are reusable solutions to commonly occurring problems in software design.", 
            "Arial", 12, false));
        
        document.addElement(new HeaderElement("Visitor Pattern", 2));
        document.addElement(new TextElement(
            "The Visitor pattern allows you to define new operations without changing existing classes.", 
            "Arial", 12, true));
        
        // Create and populate table
        TableElement table = new TableElement(3, 2, true);
        table.setCellData(0, 0, "Pattern");
        table.setCellData(0, 1, "Category");
        table.setCellData(1, 0, "Visitor");
        table.setCellData(1, 1, "Behavioral");
        table.setCellData(2, 0, "Observer");
        table.setCellData(2, 1, "Behavioral");
        document.addElement(table);
        
        document.addElement(new ImageElement("visitor-diagram.png", 400, 300, "Visitor Pattern UML Diagram", 45000));
        
        // Process document with different visitors
        System.out.println("=== Processing Document ===\\n");
        
        // Word count analysis
        WordCountVisitor wordCounter = new WordCountVisitor();
        document.processWithVisitor(wordCounter);
        wordCounter.printReport();
        
        System.out.println();
        
        // File size analysis
        FileSizeCalculatorVisitor sizeCalculator = new FileSizeCalculatorVisitor();
        document.processWithVisitor(sizeCalculator);
        sizeCalculator.printReport();
        
        System.out.println();
        
        // HTML export
        HTMLExportVisitor htmlExporter = new HTMLExportVisitor();
        document.processWithVisitor(htmlExporter);
        System.out.println("=== HTML Export ===");
        System.out.println(htmlExporter.getHTML());
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Add new operations without modifying existing classes</li>
            <li style={styles.listItem}><strong>Single Responsibility:</strong> Operations are separated from object structure</li>
            <li style={styles.listItem}><strong>Centralized Operations:</strong> Related operations are grouped in visitor classes</li>
            <li style={styles.listItem}><strong>Type Safety:</strong> Compile-time checking of operations on specific types</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to perform operations on objects with different interfaces</li>
            <li style={styles.listItem}>When you frequently add new operations but rarely add new element types</li>
            <li style={styles.listItem}>When you want to keep related operations together</li>
            <li style={styles.listItem}>When you need to perform operations across a complex object structure</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Visitor pattern is powerful for scenarios where you need to perform various operations 
            on a stable object hierarchy. It's commonly used in compilers, document processors, and 
            any system that needs to perform multiple operations on complex object structures.
          </p>
        </section>
      </main>
    </article>
  );
};

export default VisitorPattern;
